// (앱) 마이 링글 > AI 분석 통계 - 내가 집중하고 싶은 학습 영역
import { differenceInMonths, format, isDate, isValid, subMonths } from 'date-fns'
import { useCallback, useState } from 'react'
import { HIGHLIGHT_METRIC, SummaryHighlightPerMetricItem } from '../../../../../model/caf.interface'
import { useCafSummaryHighlightsV5 } from '../../../../../modules/apis/webview/caf'
import useOffsetDate from '../../../../../modules/hooks/webview/caf/common/useOffsetDate'
import useRNMsg from '../../../../../modules/hooks/webview/common/useRNMsg'
import { n_caf } from '../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../modules/i18n/useTranslation'
import { useLessonsStack } from '../../../../../pages/[lang]/webview/lessons'
import LoadingSpinner from '../../../../common/LoadingSpinner'
import FilterChipBtn from '../../../../new-portal/caf/common/BottomSheet/FilterChipBtn'
import SortResultList from '../../../../new-portal/caf/highlight/list/SortResultList'
import FilterSection from '../../../../new-portal/caf/statistic/FilterSection'
import WebviewStackContainer from '../../../common/WebviewStackContainer'
type SortOption = {
  label: string
  value: number
  name: string
}

// initialStartDate: startDate,
// initialEndDate: endDate,

const AIStatisticHighlightListActivity = ({ params }) => {
  const { useLessonsFlow } = useLessonsStack()
  const { pop, push } = useLessonsFlow()
  const { initialStartDate, initialEndDate, edit: initialEdit } = params
  const [edit, setEdit] = useState(() => Boolean(initialEdit))
  const { t } = useTranslation()
  const today = new Date()
  const dateWOffset = useOffsetDate()
  const defaultStartDate = dateWOffset(subMonths(today, 1))
  const defaultEndDate = today
  const { sendPostMsg } = useRNMsg()
  const options: SortOption[] = [
    { label: t(n_caf.statistic.date_chip1), value: 1, name: 'one_month' },
    { label: t(n_caf.statistic.date_chip2), value: 3, name: 'three_month' },
    { label: t(n_caf.statistic.date_chip3), value: 6, name: 'six_month' },
    { label: t(n_caf.statistic.date_chip4), value: 12, name: 'one_year' },
    { label: t(n_caf.statistic.date_title), value: 0, name: 'custom' },
  ]
  const [startDate, setStartDate] = useState<string | Date>(
    isValid(new Date(initialStartDate)) ? new Date(initialStartDate) : defaultStartDate
  )
  const [endDate, setEndDate] = useState<string | Date>(
    isValid(new Date(initialEndDate)) ? new Date(initialEndDate) : defaultEndDate
  )
  const [selectedFilterId, setSelectedFilterId] = useState('recommended')

  const { data, isLoading, refetch } = useCafSummaryHighlightsV5({
    startDate: startDate as string,
    endDate: endDate as string,
    pinned: false,
    recommended: false,
  })

  const mutateData = useCallback(
    (dt): SummaryHighlightPerMetricItem[] =>
      dt?.map((item) => {
        return {
          ...item,
          metric: HIGHLIGHT_METRIC[item.tag], //map
          active: item.average_count > 0,
        }
      }),
    []
  )

  const formatDate = (date: string | Date) => {
    if (isValid(new Date(date))) {
      return format(new Date(date), 'yyyy.M.d')
    }
  }

  const monthDiff = differenceInMonths(endDate as Date, startDate as Date)
  const [timePeriod, setTimePeriod] = useState<number>(monthDiff)
  const handleEdit = () => {
    setEdit((prev) => !prev)
  }

  const onChangeFilter = (option) => {
    setSelectedFilterId(option)
  }

  const filteredPinnedNum = data?.filter((item) => item.is_pinned)?.length

  return (
    <div>
      <WebviewStackContainer
        height="52px"
        onBack={pop}
        title={edit ? t(n_caf.highlight_list_edit.title) : t(n_caf.highlight_list.title)}
        border={true}
        appendRight={() => (
          <div className="fixed right-[17px] z-[999]" onClick={handleEdit}>
            {edit ? (
              <span className="text-14 text-gray-700">{t(n_caf.highlight_list_edit.done)}</span>
            ) : (
              <span className="text-14 text-gray-700">{t(n_caf.statistic.less_list.edit)}</span>
            )}
          </div>
        )}
      >
        <>
          <div className="mt-[20px] ml-[20px] flex flex-row gap-[8px]">
            <FilterSection
              timePeriod={timePeriod}
              setTimePeriod={setTimePeriod}
              startDate={startDate as Date}
              endDate={endDate as Date}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              dropdown
            />
            <FilterChipBtn state={[selectedFilterId, onChangeFilter]} dropdown />
          </div>
          <div className="mt-[12px] ml-[20px]">
            {edit ? (
              <span className="text-14 text-gray-700">{t(n_caf.highlight_list_edit.count(filteredPinnedNum))}</span>
            ) : (
              <div className="w-full flex-row items-center justify-start">
                {isDate(startDate) && isDate(endDate) && (
                  <div className="Body2-14Md flex items-start justify-start text-gray-700">
                    {formatDate(format(startDate as Date, 'yyyy-MM-dd'))} -{' '}
                    {formatDate(format(endDate as Date, 'yyyy-MM-dd'))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="mt-[12px]">
            {edit && (
              <div className="border-1 text-14 mx-20 mb-24 flex justify-center rounded-md border-gray-200 bg-gray-50 py-12 text-gray-700">
                {t(n_caf.highlight_list_edit.info)}
              </div>
            )}
            {isLoading ? (
              <div className="flex min-h-[200px] items-center">
                <LoadingSpinner lg primary show />
              </div>
            ) : (
              <div className="px-20">
                <SortResultList
                  filterId={selectedFilterId}
                  dataArr={mutateData(data)}
                  edit={edit}
                  refetch={refetch}
                  from="statistic"
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            )}
          </div>
        </>
      </WebviewStackContainer>
    </div>
  )
}

export default AIStatisticHighlightListActivity
